import React, { useState, useEffect } from 'react';
import { 
  getDashboardStats, 
  getContacts, 
  getTrainings, 
  getApplications
} from '../services/api';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalContacts: 0,
    pendingContacts: 0,
    totalTrainings: 0,
    pendingTrainings: 0,
    totalCareers: 0,
    pendingCareers: 0
  });
  
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentTrainings, setRecentTrainings] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchAllData();
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsData, contactsData, trainingsData, careersData] = await Promise.all([
        getDashboardStats(),
        getContacts(),
        getTrainings(),
        getApplications()
      ]);
      
      setStats({
        totalContacts: statsData.totalContacts || 0,
        pendingContacts: statsData.pendingContacts || 0,
        totalTrainings: statsData.totalEnrollments || 0,
        pendingTrainings: statsData.pendingEnrollments || 0,
        totalCareers: statsData.totalApplications || 0,
        pendingCareers: statsData.pendingApplications || 0
      });
      
      setRecentContacts(contactsData.slice(0, 5));
      setRecentTrainings(trainingsData.slice(0, 5));
      setRecentApplications(careersData.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #E2E8F0', borderTopColor: '#2C7A7B', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Greeting */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: '#0A192F', marginBottom: '0.5rem', fontSize: '1.8rem' }}>
          {getGreeting()}, Admin! 👋
        </h1>
        <p style={{ color: '#5A6E7F' }}>
          {formatDate(currentTime)} - Here's what's happening with your website today.
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #2C7A7B 0%, #4A9D9E 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📧</div>
              <h3 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{stats.totalContacts}</h3>
              <p>Total Contacts</p>
              {stats.pendingContacts > 0 && (
                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.9 }}>{stats.pendingContacts} pending</p>
              )}
            </div>
            <i className="fas fa-envelope" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #F5A623 0%, #F7B84D 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎓</div>
              <h3 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{stats.totalTrainings}</h3>
              <p>Training Enrollments</p>
              {stats.pendingTrainings > 0 && (
                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.9 }}>{stats.pendingTrainings} pending</p>
              )}
            </div>
            <i className="fas fa-graduation-cap" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <div>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💼</div>
              <h3 style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{stats.totalCareers}</h3>
              <p>Job Applications</p>
              {stats.pendingCareers > 0 && (
                <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.9 }}>{stats.pendingCareers} pending</p>
              )}
            </div>
            <i className="fas fa-briefcase" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
          </div>
        </div>
      </div>

      {/* Recent Activity Sections */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Recent Contacts */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#0A192F' }}>
              <i className="fas fa-envelope" style={{ color: '#2C7A7B', marginRight: '0.5rem' }}></i>
              Recent Contacts
            </h3>
            <a href="/admin/inquiries" style={{ color: '#2C7A7B', textDecoration: 'none', fontSize: '0.8rem' }}>
              View All <i className="fas fa-arrow-right"></i>
            </a>
          </div>
          {recentContacts.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#5A6E7F', padding: '2rem' }}>No contacts yet</p>
          ) : (
            recentContacts.map(contact => (
              <div key={contact._id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <div style={{ fontWeight: 500, color: '#0A192F' }}>{contact.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{contact.subject}</div>
                  </div>
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    background: contact.status === 'pending' ? '#FEE2E2' : '#D4EDDA',
                    color: contact.status === 'pending' ? '#DC2626' : '#155724'
                  }}>
                    {contact.status}
                  </span>
                </div>
                <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: '0.25rem' }}>
                  {new Date(contact.createdAt).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recent Trainings */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#0A192F' }}>
              <i className="fas fa-graduation-cap" style={{ color: '#F5A623', marginRight: '0.5rem' }}></i>
              Recent Enrollments
            </h3>
            <a href="/admin/inquiries" style={{ color: '#2C7A7B', textDecoration: 'none', fontSize: '0.8rem' }}>
              View All <i className="fas fa-arrow-right"></i>
            </a>
          </div>
          {recentTrainings.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#5A6E7F', padding: '2rem' }}>No enrollments yet</p>
          ) : (
            recentTrainings.map(training => (
              <div key={training._id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ fontWeight: 500, color: '#0A192F' }}>{training.fullName}</div>
                <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{training.courseName}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem' }}>
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    background: training.status === 'pending' ? '#FEE2E2' : '#D4EDDA',
                    color: training.status === 'pending' ? '#DC2626' : '#155724'
                  }}>
                    {training.status}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                    {new Date(training.enrolledAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Recent Applications */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ color: '#0A192F' }}>
              <i className="fas fa-briefcase" style={{ color: '#DC2626', marginRight: '0.5rem' }}></i>
              Recent Applications
            </h3>
            <a href="/admin/inquiries" style={{ color: '#2C7A7B', textDecoration: 'none', fontSize: '0.8rem' }}>
              View All <i className="fas fa-arrow-right"></i>
            </a>
          </div>
          {recentApplications.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#5A6E7F', padding: '2rem' }}>No applications yet</p>
          ) : (
            recentApplications.map(app => (
              <div key={app._id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #E2E8F0' }}>
                <div style={{ fontWeight: 500, color: '#0A192F' }}>{app.fullName}</div>
                <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>{app.position}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem' }}>
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.2rem 0.5rem',
                    borderRadius: '4px',
                    background: app.status === 'pending' ? '#FEE2E2' : '#D4EDDA',
                    color: app.status === 'pending' ? '#DC2626' : '#155724'
                  }}>
                    {app.status}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                    {new Date(app.appliedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Stats Footer */}
      <div style={{
        marginTop: '2rem',
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        gap: '1rem',
        textAlign: 'center'
      }}>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>Total Contacts</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2C7A7B' }}>{stats.totalContacts}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>Training Enrollments</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F5A623' }}>{stats.totalTrainings}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>Job Applications</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#DC2626' }}>{stats.totalCareers}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.8rem', color: '#5A6E7F' }}>Pending Items</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#F5A623' }}>
            {stats.pendingContacts + stats.pendingTrainings + stats.pendingCareers}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;